import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getMolecule } from '@peruselab/api-client/molecules';
import { getIsomer, listIsomersByMolecule, type IsomerSummary } from '@peruselab/api-client/isomers';
import { getMoleculeDrawing } from '@peruselab/api-client/moleculeDrawings';
import { getIllustration } from '@peruselab/api-client/illustrations';
import { getReactionWithParticipants } from '@peruselab/api-client/reactions';
import { paperFromReaction, type ReactionOnPaper } from './reactionOnPaper';
import { generateIsomerDepiction } from '@peruselab/structure-editor/chemistry/isomerDepiction';
import { reconstructDrawingDocument } from '@peruselab/structure-editor/domain/moleculeDrawingProjection';
import { validateDrawingDocument } from '@peruselab/structure-editor/domain/graphDrawing';
import { copyFragment, pasteFragment, placeAlongside } from '@peruselab/structure-editor/domain/clipboard';
import { REACTION_ZONES } from '@peruselab/structure-editor/domain/reactionGraph';
import type { PaperSize } from '@peruselab/structure-editor/domain/geometry';
import type { DrawingDocument } from '@peruselab/structure-editor/model';
import type { PaperAdditions } from '../components/panels/paperAdditions';
import { moleculeCardDepiction } from '../components/moleculeCardDepiction';
import type { PaperPlacement } from '../dnd/payloads';

/** A depicted molecule, or one of its registered isomers (stereo and all). */
async function depictMolecule(moleculeId: string, isomerId: string | undefined, paperSize: PaperSize) {
  const molecule = await getMolecule(moleculeId);
  return isomerId
    ? generateIsomerDepiction(molecule.document, (await getIsomer(isomerId)).document, paperSize)
    : moleculeCardDepiction(moleculeId, molecule.document, paperSize);
}

/** Several depicted structures as one drawing, side by side (so they are added as one step). */
function sideBySide(pieces: readonly DrawingDocument[]): DrawingDocument | null {
  const [first, ...rest] = pieces;
  if (!first) return null;
  return rest.reduce((combined, piece) => {
    const fragment = copyFragment(
      piece,
      piece.structure.graph.nodes.map((node) => node.id)
    );
    return fragment ? pasteFragment(combined, fragment, placeAlongside(combined, fragment)).document : combined;
  }, first);
}

/**
 * #689/#700 -- what can be put on the paper from the results and My drawings: a molecule
 * (isomer-aware), a saved drawing's structure, or an illustration's structures -- each added in
 * one undo step, left selected, where it was dropped, into a reaction box, or beside the drawing.
 */
export function usePaperAdditions(
  addStructures: (drawn: DrawingDocument, placement?: PaperPlacement) => void,
  paperSize: PaperSize,
  hasReactionArrow: boolean,
  placeReaction: (laid: ReactionOnPaper) => void
): PaperAdditions {
  const { t } = useTranslation();

  const addMolecule = useCallback(
    async (moleculeId: string, isomerId?: string, placement?: PaperPlacement) => {
      let chosen = isomerId;
      if (!chosen) {
        const isomers: readonly IsomerSummary[] = await listIsomersByMolecule(moleculeId);
        if (isomers.length > 1) return isomers;
        chosen = isomers[0]?.id;
      }
      addStructures(await depictMolecule(moleculeId, chosen, paperSize), placement);
      return null;
    },
    [addStructures, paperSize]
  );

  const addDrawing = useCallback(
    async (drawingId: string, placement?: PaperPlacement) => {
      const drawing = await getMoleculeDrawing(drawingId);
      const [ref] = drawing.document.isomerRefs;
      const isomer = ref ? await getIsomer(ref.isomerId) : null;
      const molecule = isomer ? await getMolecule(isomer.moleculeId) : null;
      addStructures(
        validateDrawingDocument(reconstructDrawingDocument(drawing.document, molecule?.document, isomer?.document)),
        placement
      );
    },
    [addStructures]
  );

  const addIllustration = useCallback(
    async (illustrationId: string, placement?: PaperPlacement) => {
      const illustration = await getIllustration(illustrationId);
      // Its molecules and isomers; a reaction element is a reaction, not a structure to add.
      const pieces = await Promise.all(
        illustration.document.elements.flatMap((element) => {
          if (element.entityType === 'isomer') {
            return [getIsomer(element.id).then((isomer) => depictMolecule(isomer.moleculeId, element.id, paperSize))];
          }
          if (element.entityType === 'molecule') return [depictMolecule(element.id, undefined, paperSize)];
          return [];
        })
      );
      const combined = sideBySide(pieces);
      if (combined) addStructures(combined, placement);
    },
    [addStructures, paperSize]
  );

  // #744 -- a whole reaction, laid out as when it is opened (its boxes, conditions, catalysts).
  const addReaction = useCallback(
    async (reactionId: string) => {
      placeReaction(await paperFromReaction(await getReactionWithParticipants(reactionId), paperSize));
    },
    [paperSize, placeReaction]
  );

  const boxes = useMemo(
    () => (hasReactionArrow ? REACTION_ZONES.map((zone) => ({ zone, label: t(`reactionZones.${zone}`) })) : null),
    [hasReactionArrow, t]
  );

  return useMemo(
    () => ({ addMolecule, addDrawing, addIllustration, addReaction, boxes }),
    [addDrawing, addIllustration, addMolecule, addReaction, boxes]
  );
}
